// Breaking Page Skeleton - No anonymous functions
import { Box, Container, Skeleton } from '@mui/material';

import { BreakingMarketCardSkeleton } from './BreakingMarketCardSkeleton';

// Pre-computed arrays
const TAB_SKELETON_WIDTHS = [60, 80, 70, 75, 65, 70, 55, 75];
const MARKET_SKELETON_ITEMS = [1, 2, 3, 4];
const NEWS_SKELETON_ITEMS = [1, 2, 3, 4, 5];

// Extracted components
const TabSkeletonItem = ({ width }: { width: number }) => (
  <Skeleton variant="rounded" width={width} height={40} sx={{ borderRadius: 2 }} />
);

// Mirrors BreakingBanner's StatTile — boxed tile on sm+, plain inline numbers on mobile.
const StatTileSkeleton = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'row', sm: 'column' },
      alignItems: { xs: 'center', sm: 'flex-start' },
      gap: { xs: '6px', sm: 0.5 },
      width: { xs: 'auto', sm: 132 },
      p: { xs: 0, sm: '20px' },
      borderRadius: { xs: 0, sm: '16px' },
      borderWidth: { xs: 0, sm: '1px' },
      borderStyle: 'solid',
      borderColor: 'divider',
    }}
  >
    <Skeleton variant="circular" width={20} height={20} sx={{ flexShrink: 0 }} />
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'row', sm: 'column' },
        gap: { xs: 0.75, sm: 0.5 },
      }}
    >
      <Skeleton variant="text" width={36} height={22} />
      <Skeleton variant="text" width={50} height={14} />
    </Box>
  </Box>
);

const NewsSkeletonItem = () => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
      <Skeleton variant="circular" width={8} height={8} sx={{ mt: 0.75 }} />
      <Skeleton variant="text" width="80%" height={20} />
    </Box>
    <Skeleton variant="text" width="95%" height={18} />
    <Skeleton variant="text" width="70%" height={18} sx={{ mb: 0.5 }} />
    <Skeleton variant="text" width={120} height={16} />
  </Box>
);

export const BreakingSkeleton = () => {
  const renderedTabSkeletons = TAB_SKELETON_WIDTHS.map((width, i) => (
    <TabSkeletonItem key={i} width={width} />
  ));

  const renderedMarketSkeletons = MARKET_SKELETON_ITEMS.map((item, i) => (
    <BreakingMarketCardSkeleton key={item} showDivider={i < MARKET_SKELETON_ITEMS.length - 1} />
  ));

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ display: 'flex', gap: 3, flexDirection: 'column' }}>
        {/* Main Content */}
        <Box sx={{ flex: 1 }}>
          {/* Hero Section — plain on mobile (no card chrome), boxed on sm+ */}
          <Box
            sx={{
              borderWidth: { xs: 0, sm: '1px' },
              borderStyle: 'solid',
              borderColor: 'divider',
              borderRadius: { xs: 0, sm: '20px' },
              p: { xs: 0, sm: '32px', md: '40px 44px' },
              mb: '24px',
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: '24px', md: '40px' },
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton variant="text" width={120} height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="40%" height={48} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="60%" height={24} />
            </Box>
            <Box sx={{ display: 'flex', gap: { xs: '20px', sm: '14px' }, flexShrink: 0 }}>
              <StatTileSkeleton />
              <StatTileSkeleton />
            </Box>
          </Box>

          {/* Category Tabs */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 3,
              pb: 1,
              borderBottom: 1,
              borderColor: 'divider',
              overflowX: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
            }}
          >
            {renderedTabSkeletons}
          </Box>

          {/* Markets List */}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '16px',
              overflow: 'hidden',
            }}
          >
            {renderedMarketSkeletons}
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default BreakingSkeleton;
